/**
 * A game controller, an Xbox controller or any the browser maps as its
 * "standard" layout, as the game's controls: which keys its buttons hold
 * down, what its sticks and triggers ask for, and how far it turns the view.
 *
 * The buttons are keys, so everything the keyboard does a button does too,
 * as the keyboard does it (`three/gamepad.ts` presses and lets go of them);
 * the sticks and triggers are by how much (`PlayerController.pad`).
 *
 *            on foot          car, motorbike     helicopter        falling
 *   L stick  walk             steer              cyclic            dive, glide
 *   R stick  look; holding    look               look              look
 *            something, ↑↓
 *            raise and lower
 *   RT / LT  right / left     throttle / brake,  up / down         -
 *            punch            reverse
 *   A        jump             handbrake          -                 open canopy
 *   B, L3    run (held)       main beam          -                 -
 *   X        camera           camera             camera            camera
 *   Y        get in           get out            get out (landed)  -
 *   LB       pick up / drop   horn (held)        yaw left          -
 *   RB       helicopter       radio              yaw right         -
 *   D-pad    ↑ job  ↓ parachute (on foot), fill up (in a machine)
 *            ← police  → jumps (motorbike)
 *   View     minimap          Menu  world map
 *
 * Pure: buttons and axes in, keys and amounts out.
 */

export type PadMode = 'walk' | 'drive' | 'moto' | 'heli' | 'freefall';

/** The standard layout's buttons, by index. */
export const BUTTON = {
  a: 0, b: 1, x: 2, y: 3, lb: 4, rb: 5, lt: 6, rt: 7, view: 8, menu: 9, l3: 10, r3: 11,
  up: 12, down: 13, left: 14, right: 15,
} as const;

/** How far a stick must move from the middle to count, of the way to its edge. */
export const DEADZONE = 0.16;
/** A trigger pulled this far is a button pressed. */
const TRIGGER = 0.5;
/** How fast the right stick turns the view at full tilt, as pointer pixels a second. */
export const LOOK_SPEED = 1100;

export interface PadFrame {
  /** The keys its buttons hold down now. */
  keys: Set<string>;
  moveX: number;
  moveY: number;
  throttle: number;
  steer: number;
  yaw: number;
  collective: number;
  /** The right stick: how much to turn the view, -1..1 each way. */
  lookX: number;
  lookY: number;
}

/** A stick's two axes with the dead middle taken out, the rest stretched back over 0..1, round. */
export function deadzone(x: number, y: number, zone = DEADZONE): [number, number] {
  const r = Math.hypot(x, y);
  if (r < zone) return [0, 0];
  const k = Math.min(1, (r - zone) / (1 - zone)) / r;
  return [x * k, y * k];
}

/** The controller's state as the game's controls, in `mode`. `buttons` each 0..1 (a trigger by how far), `axes` -1..1. */
export function padFrame(buttons: readonly number[], axes: readonly number[], mode: PadMode): PadFrame {
  const b = (i: number) => (buttons[i] ?? 0) > TRIGGER;
  const [lx, ly] = deadzone(axes[0] ?? 0, axes[1] ?? 0);
  const [rx, ry] = deadzone(axes[2] ?? 0, axes[3] ?? 0);
  const triggers = (buttons[BUTTON.rt] ?? 0) - (buttons[BUTTON.lt] ?? 0);
  const keys = new Set<string>();
  const hold = (button: number, code: string) => { if (b(button)) keys.add(code); };

  hold(BUTTON.x, 'KeyC');
  hold(BUTTON.view, 'KeyM');
  hold(BUTTON.menu, 'KeyN');
  hold(BUTTON.up, 'KeyY');
  hold(BUTTON.left, 'KeyK');
  const machine = mode === 'drive' || mode === 'moto';
  if (mode === 'walk') {
    hold(BUTTON.a, 'Space');
    hold(BUTTON.b, 'ShiftLeft');
    hold(BUTTON.l3, 'ShiftLeft');
    hold(BUTTON.y, 'KeyE');
    hold(BUTTON.lt, 'PunchLeft');
    hold(BUTTON.rt, 'PunchRight');
    hold(BUTTON.lb, 'Grab');
    hold(BUTTON.rb, 'KeyH');
    hold(BUTTON.down, 'KeyP');
  } else if (machine) {
    hold(BUTTON.a, 'Space');
    hold(BUTTON.b, 'ShiftLeft');
    hold(BUTTON.y, 'KeyE');
    hold(BUTTON.lb, 'KeyG');
    hold(BUTTON.rb, 'KeyR');
    hold(BUTTON.down, 'KeyF');
    if (mode === 'moto') hold(BUTTON.right, 'KeyJ');
  } else if (mode === 'heli') {
    hold(BUTTON.y, 'KeyH');
    hold(BUTTON.down, 'KeyF');
  } else {
    hold(BUTTON.a, 'Space');
  }
  return {
    keys,
    // A stick pushed up reads negative.
    moveX: mode === 'walk' || mode === 'heli' || mode === 'freefall' ? lx : 0,
    moveY: mode === 'walk' || mode === 'heli' || mode === 'freefall' ? -ly : 0,
    throttle: machine ? triggers : 0,
    steer: machine ? lx : 0,
    yaw: mode === 'heli' ? (b(BUTTON.rb) ? 1 : 0) - (b(BUTTON.lb) ? 1 : 0) : 0,
    collective: mode === 'heli' ? triggers : 0,
    lookX: rx,
    lookY: ry,
  };
}
